import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { t as tippy } from './tippy-headless.esm.js';
import { F as FormField } from './form-field.js';
import { d as getAriaExpanded, c as getAriaDisabled, b as getAriaRequired, a as getAriaInvalid, g as getAriaDescribedBy } from './utils.js';
import { d as defineCustomElement$8 } from './cosmos-button2.js';
import { d as defineCustomElement$7 } from './cosmos-icon-arrow-down2.js';
import { d as defineCustomElement$6 } from './cosmos-icon-close2.js';
import { d as defineCustomElement$5 } from './cosmos-icon-warning2.js';
import { d as defineCustomElement$4 } from './cosmos-select-value2.js';
import { d as defineCustomElement$3 } from './cosmos-spinner2.js';
import { d as defineCustomElement$2 } from './cosmos-text2.js';
import { b as bem } from './bem.js';
import { n as nextTick } from './nextTick.js';
import { i as isNodeEnvironment } from './isNodeEnvironment.js';
import { g as global } from './global.js';
import { t as transformTagName, r as resolveAppearance } from './transformTagName.js';

const interceptEvent = (event) => {
    event.preventDefault();
    event.stopPropagation();
};
/**
 * Ensure that the focus is consistently reset to the element provided.
 * Repeatedly blurring and focusing the element forces VoiceOver on iOS to
 * regain focus on the element.
 */
const forceFocus = async function (element, retries = 5) {
    element.addEventListener('focus', interceptEvent);
    element.addEventListener('blur', interceptEvent);
    for (let index = 0; index < retries; index++) {
        await nextTick();
        element.blur();
        element.focus();
    }
    element.blur();
    element.removeEventListener('focus', interceptEvent);
    element.removeEventListener('blur', interceptEvent);
    element.focus();
};

const cosmosSelectCss = "@charset \"UTF-8\";.cosmos-form-field{display:-ms-flexbox;display:flex;-ms-flex-direction:column;flex-direction:column}.cosmos-form-field ::slotted(:not(:first-of-type,:only-of-type)){margin-top:12px}.cosmos-form-field__info-container{display:inline-block;margin-bottom:8px}.cosmos-form-field__label-text,.cosmos-form-field__help-text,.cosmos-form-field__required-optional-label,.cosmos-form-field__validation-message{display:inline}.cosmos-form-field__label-text{--cosmos-text-color:var(--cosmos-select-label-color);--cosmos-text-font-family:var(--cosmos-select-label-font-family);--cosmos-text-font-size:var(--cosmos-select-label-font-size);--cosmos-text-font-style:var(--cosmos-select-label-font-style);--cosmos-text-font-weight:var(--cosmos-select-label-font-weight);--cosmos-text-letter-spacing:var(--cosmos-select-label-letter-spacing);--cosmos-text-line-height:var(--cosmos-select-label-line-height);--cosmos-text-text-transform:var(--cosmos-select-label-text-transform)}.cosmos-form-field__help-text{--cosmos-text-color:var(--cosmos-select-help-text-color);--cosmos-text-font-family:var(--cosmos-select-help-text-font-family);--cosmos-text-font-size:var(--cosmos-select-help-text-font-size);--cosmos-text-font-style:var(--cosmos-select-help-text-font-style);--cosmos-text-font-weight:var(--cosmos-select-help-text-font-weight);--cosmos-text-letter-spacing:var(--cosmos-select-help-text-letter-spacing);--cosmos-text-line-height:var(--cosmos-select-help-text-line-height);--cosmos-text-text-transform:var(--cosmos-select-help-text-text-transform)}.cosmos-form-field__required-optional-label{--cosmos-text-color:var(--cosmos-select-required-optional-label-color);--cosmos-text-font-family:var(--cosmos-select-required-optional-label-font-family);--cosmos-text-font-size:var(--cosmos-select-required-optional-label-font-size);--cosmos-text-font-style:var(--cosmos-select-required-optional-label-font-style);--cosmos-text-font-weight:var(--cosmos-select-required-optional-label-font-weight);--cosmos-text-letter-spacing:var(--cosmos-select-required-optional-label-letter-spacing);--cosmos-text-line-height:var(--cosmos-select-required-optional-label-line-height);--cosmos-text-text-transform:var(--cosmos-select-required-optional-label-text-transform);display:inline-block;-webkit-margin-start:4px;margin-inline-start:4px;white-space:nowrap}.cosmos-form-field__validation-message{display:-ms-flexbox;display:flex;gap:4px;margin-top:8px}.cosmos-form-field__validation-message-text{--cosmos-text-font-family:var(--cosmos-select-validation-message-font-family);--cosmos-text-font-size:var(--cosmos-select-validation-message-font-size);--cosmos-text-font-style:var(--cosmos-select-validation-message-font-style);--cosmos-text-font-weight:var(--cosmos-select-validation-message-font-weight);--cosmos-text-letter-spacing:var(--cosmos-select-validation-message-letter-spacing);--cosmos-text-line-height:var(--cosmos-select-validation-message-line-height);--cosmos-text-text-transform:var(--cosmos-select-validation-message-text-transform)}.cosmos-form-field__validation-message-icon{--cosmos-icon-color:var(--cosmos-select-invalid-color, #E06600);--cosmos-icon-size:calc(19rem / var(--cosmos-base-font-size, 16));-ms-flex-negative:0;flex-shrink:0}.cosmos-form-field--invalid .cosmos-form-field__label-text,.cosmos-form-field--invalid .cosmos-form-field__validation-message{--cosmos-text-color:var(--cosmos-select-label-color)}.cosmos-form-field--disabled.cosmos-form-field--appearance-dark .cosmos-form-field__label-text{--cosmos-text-color:var(--cosmos-select-label-color, rgba(0, 15, 30, 0.45))}.cosmos-form-field--disabled.cosmos-form-field--appearance-dark .cosmos-form-field__help-text{--cosmos-text-color:var(--cosmos-select-help-text-color, rgba(0, 15, 30, 0.45))}.cosmos-form-field--disabled.cosmos-form-field--appearance-dark .cosmos-form-field__required-optional-label{--cosmos-text-color:var(--cosmos-select-required-optional-label-color, rgba(0, 15, 30, 0.45))}.cosmos-form-field--disabled.cosmos-form-field--appearance-light .cosmos-form-field__label-text{--cosmos-text-color:var(--cosmos-select-label-color, rgba(255, 255, 255, 0.45))}.cosmos-form-field--disabled.cosmos-form-field--appearance-light .cosmos-form-field__help-text{--cosmos-text-color:var(--cosmos-select-help-text-color, rgba(255, 255, 255, 0.45))}.cosmos-form-field--disabled.cosmos-form-field--appearance-light .cosmos-form-field__required-optional-label{--cosmos-text-color:var(--cosmos-select-required-optional-label-color, rgba(0, 15, 30, 0.45))}:host{--cosmos-font-family-text:Bull Text, Helvetica, sans-serif}@supports (font-variation-settings: normal){:host{--cosmos-font-family-text:Bull VF, Helvetica, sans-serif}}:host(:lang(ja)){--cosmos-font-family-text:Noto Sans JP, Helvetica, sans-serif}:host(:lang(ar)){--cosmos-font-family-text:Noto Kufi Arabic, Helvetica, sans-serif}:host(:lang(ko)){--cosmos-font-family-text:Noto Sans KR, Helvetica, sans-serif}:host(:lang(hi)){--cosmos-font-family-text:Noto Sans, Helvetica, sans-serif}:host(:lang(he)){--cosmos-font-family-text:Noto Sans Hebrew, Helvetica, sans-serif}:host(:lang(ka)){--cosmos-font-family-text:Noto Sans Georgian, Helvetica, sans-serif}:host(:lang(th)){--cosmos-font-family-text:Noto Sans Thai, Helvetica, sans-serif}:host(:lang(zh)){--cosmos-font-family-text:Microsoft YaHei, ヒラギノ角ゴ Pro W3, Hiragino Kaku Gothic Pro, Hiragino Kaku Gothic ProN W3, Osaka, メイリオ, Meiryo, ＭＳ Ｐゴシック, MS PGothic, Arial Unicode MS, Tahoma, Helvetica, sans-serif}:host(:lang(zh-Hant)){--cosmos-font-family-text:Microsoft JhengHei, Geneva CY, ヒラギノ角ゴ Pro W3, Hiragino Kaku Gothic Pro, Hiragino Kaku Gothic ProN W3, Osaka, メイリオ, Meiryo, ＭＳ Ｐゴシック, MS PGothic, Arial Unicode MS, Tahoma, Helvetica, sans-serif}:host{display:block;width:100%}.cosmos-select{position:relative}.cosmos-select__input{--cosmos-text-color:var(--cosmos-select-color);--cosmos-text-font-family:var(--cosmos-select-font-family);--cosmos-text-font-size:var(--cosmos-select-font-size);--cosmos-text-font-style:var(--cosmos-select-font-style);--cosmos-text-font-weight:var(--cosmos-select-font-weight);-ms-flex-align:center;align-items:center;border:solid var(--cosmos-select-border-width, 1px);border-radius:var(--cosmos-select-border-radius, 4px);-webkit-box-sizing:border-box;box-sizing:border-box;display:-ms-flexbox;display:flex;gap:8px;-ms-flex-pack:justify;justify-content:space-between;letter-spacing:var(--cosmos-select-letter-spacing, normal);max-width:100%;padding:var(--cosmos-select-padding, 0 12px);position:relative;text-transform:var(--cosmos-select-text-transform, none);-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;width:100%}.cosmos-select__input--invalid:not(.cosmos-select__input--disabled){border-color:var(--cosmos-select-invalid-color, #E06600);-webkit-box-shadow:inset 0 0 0 1px var(--cosmos-select-invalid-color, #E06600);box-shadow:inset 0 0 0 1px var(--cosmos-select-invalid-color, #E06600)}.cosmos-select__input--appearance-dark{background-color:var(--cosmos-select-background-color, #FFFFFF);border-color:var(--cosmos-select-border-color, rgba(0, 15, 30, 0.45));color:var(--cosmos-select-color, #000F1E)}.cosmos-select__input--appearance-dark.cosmos-select__input--placeholder{--cosmos-text-color:var(--cosmos-select-placeholder-color)}.cosmos-select__input--appearance-dark.cosmos-select__input--disabled{background:rgba(0, 15, 30, 0.05);border-color:transparent;color:var(--cosmos-select-color, rgba(0, 15, 30, 0.45))}.cosmos-select__input--appearance-dark.cosmos-select__input--disabled.cosmos-select__input--placeholder{--cosmos-text-color:custom-select-property(\"color\", $cosmos-color-surface-glass-dark-45)}.cosmos-select__input--appearance-dark:not(.cosmos-select__input--disabled) .cosmos-select__icon{color:var(--cosmos-select-arrow-color, #000F1E)}.cosmos-select__input--appearance-dark:hover:not(.cosmos-select__input--disabled):not(.cosmos-select__input--invalid){background-color:var(--cosmos-select-background-color-hover, #FFFFFF);border-color:var(--cosmos-select-border-color-hover, rgba(0, 15, 30, 0.6))}.cosmos-select__input--appearance-dark:hover:not(.cosmos-select__input--disabled):not(.cosmos-select__input--invalid):not(.cosmos-select__input--placeholder){color:var(--cosmos-select-color-hover, #000F1E)}.cosmos-select__input--appearance-light{background-color:var(--cosmos-select-background-color, rgba(255, 255, 255, 0.05));border-color:var(--cosmos-select-border-color, rgba(255, 255, 255, 0.45));color:var(--cosmos-select-color, #FFFFFF)}.cosmos-select__input--appearance-light.cosmos-select__input--placeholder{--cosmos-text-color:var(--cosmos-select-placeholder-color)}.cosmos-select__input--appearance-light.cosmos-select__input--disabled{background:rgba(255, 255, 255, 0.05);border-color:transparent;color:var(--cosmos-select-color, rgba(255, 255, 255, 0.45))}.cosmos-select__input--appearance-light.cosmos-select__input--disabled.cosmos-select__input--placeholder{--cosmos-text-color:custom-select-property(\"color\", $cosmos-color-surface-glass-light-45)}.cosmos-select__input--appearance-light:not(.cosmos-select__input--disabled) .cosmos-select__icon{color:var(--cosmos-select-arrow-color, #FFFFFF)}.cosmos-select__input--appearance-light:hover:not(.cosmos-select__input--disabled):not(.cosmos-select__input--invalid){background-color:var(--cosmos-select-background-color-hover, rgba(255, 255, 255, 0.05));border-color:var(--cosmos-select-border-color-hover, rgba(255, 255, 255, 0.6))}.cosmos-select__input--appearance-light:hover:not(.cosmos-select__input--disabled):not(.cosmos-select__input--invalid):not(.cosmos-select__input--placeholder){color:var(--cosmos-select-color-hover, #FFFFFF)}.cosmos-select__icon{color:var(--cosmos-select-arrow-color, currentColor);-ms-flex:0 0 auto;flex:0 0 auto}.cosmos-select__combobox{-ms-flex-align:center;align-items:center;display:-ms-flexbox;display:flex;-ms-flex:1 1 auto;flex:1 1 auto;height:calc(38rem / var(--cosmos-base-font-size, 16));overflow:hidden}.cosmos-select__combobox:focus,.cosmos-select__combobox:focus:not(:focus-visible){outline:none}.cosmos-select__combobox~.cosmos-select__focus-ring{display:none;pointer-events:none}.cosmos-select__combobox:focus~.cosmos-select__focus-ring{border-radius:calc(var(--cosmos-select-border-radius, 4px) * 1.5);bottom:calc((4px + 0px) * -1);-webkit-box-shadow:inset 0 0 0 2px #1B6AEE, 0 0 0 2px #FFFFFF;box-shadow:inset 0 0 0 2px #1B6AEE, 0 0 0 2px #FFFFFF;left:calc((4px + 0px) * -1);position:absolute;right:calc((4px + 0px) * -1);top:calc((4px + 0px) * -1);display:block}.cosmos-select__listbox{background-color:var(--cosmos-select-options-background-color, #FFFFFF);border-radius:var(--cosmos-select-options-border-radius, 4px);-webkit-box-shadow:0px 16px 24px 0px rgba(0, 15, 30, 0.0588235294), 0px 4px 8px 0px rgba(0, 15, 30, 0.0196078431);box-shadow:0px 16px 24px 0px rgba(0, 15, 30, 0.0588235294), 0px 4px 8px 0px rgba(0, 15, 30, 0.0196078431);-webkit-box-sizing:border-box;box-sizing:border-box;left:0;max-height:var(--cosmos-select-options-max-height, 320px);overflow:auto;padding:var(--cosmos-select-options-padding, 4px);pointer-events:none;position:absolute;right:0;top:100%;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;visibility:hidden;width:100%;z-index:1}.cosmos-select__listbox--appearance-light{background-color:#00162B;border:1px solid rgba(255, 255, 255, 0.45)}.cosmos-select__listbox--open{pointer-events:auto;visibility:visible}.cosmos-select__clearable-button{--cosmos-button-icon-size:calc(10rem / var(--cosmos-base-font-size, 16))}.cosmos-select__clearable-button::part(base){height:calc(18rem / var(--cosmos-base-font-size, 16));min-width:calc(18rem / var(--cosmos-base-font-size, 16));width:calc(18rem / var(--cosmos-base-font-size, 16))}.cosmos-select__sr-value{border-width:0;clip:rect(0, 0, 0, 0);height:1px;margin:-1px;overflow:hidden;padding:0;position:absolute;white-space:nowrap;width:1px}.cosmos-select ::slotted([slot=value]){-ms-flex:1 0 auto;flex:1 0 auto;min-width:0}";
const CosmosSelectStyle0 = cosmosSelectCss;

const generateClassNames = bem('cosmos-select');
const CosmosSelect$1 = /*@__PURE__*/ proxyCustomElement(class CosmosSelect extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.inputchangeEvent = createEvent(this, "inputchange", 6);
        this.inputinvalidEvent = createEvent(this, "inputinvalid", 3);
        // Creates dependency between the select and the select-value component.
        this.dependency = [h("cosmos-select-value", null)];
        this.PAGE_SIZE = 10;
        this.selectActions = {
            CLOSE: 1,
            CLOSE_SELECT: 2,
            FIRST: 3,
            LAST: 4,
            NEXT: 5,
            OPEN: 6,
            PAGE_DOWN: 7,
            PAGE_UP: 8,
            PREV: 9,
            SELECT: 10,
            TYPE: 11,
        };
        this.searchTerm = '';
        this.isNavigatedWithKeyboard = false;
        this.wasSubmitted = false;
        this.handleFormData = (event) => {
            var _a;
            const value = ((_a = this.selectedOptionElement) === null || _a === void 0 ? void 0 : _a.value) || '';
            if (this.name && !this.disabled) {
                return event.formData.append(this.name, value);
            }
        };
        this.handleFormSubmit = (event) => {
            this.wasSubmitted = true;
            if (this.reportValidity()) {
                event.preventDefault();
            }
        };
        this.handleDocumentClick = () => {
            if (this.isListBoxVisible) {
                // Close the listbox without resetting focus
                this.closeListBox(false);
            }
        };
        this.handleSlotChange = () => {
            this.initOptions();
        };
        this.handleListBoxClick = (event) => {
            // make sure the click on the list is not recognized as a click "outside"
            event.preventDefault();
            event.stopImmediatePropagation();
        };
        this.handleSelectClick = (event) => {
            // make sure the click on the select is not recognized as a click "outside"
            event.preventDefault();
            event.stopImmediatePropagation();
            if (this.disabled) {
                return;
            }
            if (this.isListBoxVisible) {
                this.closeListBox();
            }
            else {
                this.openListBox();
                this.activateOption(this.selectedOptionElement);
                // Ensures the combobox remains keyboard accessible
                forceFocus(this.comboBoxElement);
            }
        };
        this.handleSelectChange = (optionElement) => {
            if (!this.isInvalidStateControlled() && this.wasSubmitted) {
                this.reportValidity();
            }
            this.inputchangeEvent.emit({ value: optionElement.value });
        };
        this.handleSelectKeyDown = (event) => {
            this.isNavigatedWithKeyboard = true;
            if (this.disabled) {
                return;
            }
            const action = this.getActionFromCode(event.code, event.key, event.ctrlKey, event.altKey, event.metaKey);
            if (action) {
                if (event.key !== 'Tab') {
                    event.preventDefault();
                }
                switch (action) {
                    case this.selectActions.CLOSE:
                        this.closeListBox();
                        break;
                    case this.selectActions.CLOSE_SELECT:
                        this.selectOption(this.options[this.activeOptionIndex]);
                        this.closeListBox();
                        break;
                    case this.selectActions.OPEN:
                        this.openListBox();
                        nextTick().then(() => {
                            this.activateOption(this.selectedOptionElement || this.options[0]);
                        });
                        break;
                    case this.selectActions.PAGE_UP:
                        this.activateOption(this.options[Math.max(this.activeOptionIndex - this.PAGE_SIZE, 0)]);
                        break;
                    case this.selectActions.PAGE_DOWN:
                        this.activateOption(this.options[Math.min(this.activeOptionIndex + this.PAGE_SIZE, this.options.length - 1)]);
                        break;
                    case this.selectActions.FIRST:
                        this.openListBox();
                        nextTick().then(() => {
                            this.activateOption(this.options[0]);
                        });
                        break;
                    case this.selectActions.LAST:
                        this.openListBox();
                        nextTick().then(() => {
                            this.activateOption(this.options[this.options.length - 1]);
                        });
                        break;
                    case this.selectActions.NEXT:
                        this.activateOption(this.options[Math.min(this.activeOptionIndex + 1, this.options.length - 1)]);
                        break;
                    case this.selectActions.PREV:
                        this.activateOption(this.options[Math.max(this.activeOptionIndex - 1, 0)]);
                        break;
                    case this.selectActions.TYPE:
                        this.openListBox();
                        nextTick().then(() => {
                            this.activateMatchingOption(this.getSearchTerm(event.key));
                        });
                        break;
                }
            }
        };
        this.handleClearableClick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.clearSelect();
        };
        this.handleClearableKeyDown = (event) => {
            if (![' ', 'Enter'].includes(event.key)) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            this.clearSelect();
        };
        this.getValidationMessage = () => {
            if (this.validationMessage) {
                return this.validationMessage;
            }
            else {
                if (!isNodeEnvironment() && this.form) {
                    const ghostSelect = global.document.createElement('select');
                    ghostSelect.name = this.name;
                    ghostSelect.required = !!this.required;
                    ghostSelect.style.display = 'none';
                    this.form.appendChild(ghostSelect);
                    const message = ghostSelect.validationMessage;
                    ghostSelect.remove();
                    return message;
                }
            }
        };
        this.getSelectedOption = () => {
            if (this.value) {
                return this.options.find((option) => option.value === this.value);
            }
            return this.options.find((option) => option.selected);
        };
        this.getSelectValueSlot = () => {
            let selectedValueSlot = this.element.querySelector(transformTagName('cosmos-select-value'));
            if (!selectedValueSlot) {
                selectedValueSlot = global.document.createElement(transformTagName('cosmos-select-value'));
                selectedValueSlot.setAttribute('slot', 'value');
                this.element.prepend(selectedValueSlot);
            }
            return selectedValueSlot;
        };
        this.appearance = undefined;
        this.autofocus = undefined;
        this.clearable = undefined;
        this.clearAriaLabel = undefined;
        this.disabled = undefined;
        this.helpText = undefined;
        this.invalid = false;
        this.label = undefined;
        this.name = undefined;
        this.optionalLabel = undefined;
        this.placeholder = undefined;
        this.required = false;
        this.requiredLabel = undefined;
        this.validationMessage = undefined;
        this.value = undefined;
        this.activeOptionElement = undefined;
        this.activeOptionIndex = 0;
        this.computedAppearance = 'dark';
        this.inputInvalid = false;
        this.isListBoxVisible = false;
        this.selectedOptionElement = undefined;
        this.selectedOptionIndex = undefined;
    }
    handleInvalidChange(newValue) {
        this.inputInvalid = newValue;
    }
    handleValueChange(newValue) {
        const option = this.options.find((option) => option.value === newValue);
        this.selectOption(option, false);
    }
    handleAppearanceChange(appearance) {
        this.computedAppearance = appearance;
        this.options.forEach((el) => (el.appearance = this.computedAppearance));
        this.optgroups.forEach((el) => (el.appearance = this.computedAppearance));
    }
    /**
     * Removes focus from the Input.
     */
    async inputBlur() {
        this.comboBoxElement && this.comboBoxElement.blur();
    }
    /**
     * Sets focus on the Input. This will scroll the element into the view and focus it.
     */
    async inputFocus() {
        this.comboBoxElement && this.comboBoxElement.focus();
    }
    /**
     * Returns the validity status of the Input.
     * Fires an `inputinvalid` event in the case of invalidity.
     */
    async inputCheckValidity() {
        return this.checkValidity();
    }
    /**
     * Performs the same validity checking steps as the inputCheckValidity methods.
     * Fires an `inputinvalid` event and reports the problem to the user in case of invalidity.
     */
    async inputReportValidity() {
        return this.reportValidity();
    }
    handleOptionSelect(event) {
        this.selectOption(event.target);
        this.closeListBox();
    }
    handleKeyDown(event) {
        this.handleSelectKeyDown(event);
    }
    async closeListBox(resetFocus = true) {
        var _a;
        if (!this.isListBoxVisible)
            return;
        this.isListBoxVisible = false;
        this.isNavigatedWithKeyboard = false;
        (_a = this.tippyInstance) === null || _a === void 0 ? void 0 : _a.hide();
        global.window.removeEventListener('click', this.handleDocumentClick);
        if (resetFocus) {
            await forceFocus(this.comboBoxElement);
        }
    }
    openListBox() {
        var _a;
        this.isListBoxVisible = true;
        (_a = this.tippyInstance) === null || _a === void 0 ? void 0 : _a.show();
        global.window.addEventListener('click', this.handleDocumentClick);
    }
    checkValidity() {
        return !(this.required && !this.selectedOptionElement);
    }
    reportValidity() {
        this.inputInvalid = !this.checkValidity();
        if (this.inputInvalid) {
            this.inputinvalidEvent.emit();
        }
        return this.inputInvalid;
    }
    initAutofocus() {
        const isFirstAutofocusElementonPage = global.document.querySelector('[autofocus]') === this.element;
        if (isFirstAutofocusElementonPage) {
            setTimeout(async () => {
                await this.inputFocus();
            }, 1);
        }
    }
    isInvalidStateControlled() {
        return this.invalid;
    }
    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    selectOption(optionElement, shouldEmitEvent = true) {
        if (optionElement && !optionElement.disabled && optionElement !== this.selectedOptionElement) {
            const hasChanged = shouldEmitEvent && optionElement !== this.selectedOptionElement;
            this.selectedOptionIndex = this.options.findIndex((item) => item === optionElement);
            this.getSelectValueSlot().innerHTML = optionElement.innerHTML;
            this.selectedOptionElement = optionElement;
            this.value = optionElement.value;
            this.updateOptions();
            if (hasChanged) {
                this.handleSelectChange(optionElement);
            }
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    activateOption(optionElement) {
        if (optionElement) {
            this.activeOptionIndex = this.options.findIndex((item) => item === optionElement);
            this.activeOptionElement = optionElement;
            this.updateOptions();
        }
    }
    activateMatchingOption(searchTerm) {
        const matchingOption = this.getOptionBySearchTerm(searchTerm);
        if (matchingOption) {
            this.activateOption(matchingOption);
        }
        else {
            this.clearSearchTerm();
        }
    }
    getOptionBySearchTerm(searchTerm) {
        const filteredOptions = this.options.filter((option) => { var _a; return (_a = option.textContent) === null || _a === void 0 ? void 0 : _a.toLowerCase().startsWith(searchTerm.toLowerCase()); });
        const directMatch = filteredOptions[0];
        const allSameCharacter = searchTerm.split('').every((char) => char === searchTerm[0]);
        if (directMatch) {
            return directMatch;
        }
        /**
         * When all characters of the search term are the same (e.g. `bbb`)
         * we cycle through options that start with `b`
         */
        if (allSameCharacter) {
            // cycling through options by reordering them based on the current active option
            const orderedOptions = [
                ...this.options.slice(this.activeOptionIndex + 1),
                ...this.options.slice(0, this.activeOptionIndex + 1),
            ];
            const filteredOptions = orderedOptions.filter((option) => { var _a; return (_a = option.textContent) === null || _a === void 0 ? void 0 : _a.toLowerCase().startsWith(searchTerm[0].toLowerCase()); });
            return filteredOptions[0];
        }
    }
    getSearchTerm(key) {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.clearSearchTerm(), 500);
        this.searchTerm += key;
        return this.searchTerm;
    }
    clearSearchTerm() {
        this.searchTerm = '';
    }
    async clearSelect() {
        await forceFocus(this.comboBoxElement);
        this.activeOptionIndex = 0;
        this.selectedOptionElement = undefined;
        this.selectedOptionIndex = undefined;
        this.activeOptionElement = undefined;
        this.value = undefined;
        this.getSelectValueSlot().remove();
        this.inputchangeEvent.emit({ value: '' });
        this.updateOptions();
    }
    getActionFromCode(code, key, ctrlKey, altKey, metaKey) {
        // when open or closed
        if (code === 'Home') {
            return this.selectActions.FIRST;
        }
        if (code === 'End') {
            return this.selectActions.LAST;
        }
        if (code === 'Backspace' ||
            code === 'Clear' ||
            (key.length === 1 && key !== ' ' && !altKey && !ctrlKey && !metaKey)) {
            return this.selectActions.TYPE;
        }
        // when closed
        if (!this.isListBoxVisible) {
            if (['ArrowDown', 'Enter', 'Space'].includes(code)) {
                return this.selectActions.OPEN;
            }
            if (code === 'ArrowUp') {
                return this.selectActions.FIRST;
            }
        }
        // when open
        if (this.isListBoxVisible) {
            if (code === 'Tab') {
                return this.selectActions.CLOSE_SELECT;
            }
            if (code === 'Escape') {
                return this.selectActions.CLOSE;
            }
            if (code === 'PageDown') {
                return this.selectActions.PAGE_DOWN;
            }
            if (code === 'PageUp') {
                return this.selectActions.PAGE_UP;
            }
            if (code === 'ArrowUp' && altKey) {
                return this.selectActions.CLOSE_SELECT;
            }
            if (code === 'ArrowUp') {
                return this.selectActions.PREV;
            }
            if (code === 'ArrowDown' && !altKey) {
                return this.selectActions.NEXT;
            }
            if (code === 'Enter' || code === 'Space') {
                return this.selectActions.CLOSE_SELECT;
            }
        }
    }
    initTippy() {
        if (this.inputElement && this.listBoxElement) {
            const listBoxElement = this.listBoxElement;
            this.tippyInstance = tippy(this.inputElement, {
                appendTo: 'parent',
                trigger: 'manual',
                placement: 'bottom',
                hideOnClick: false,
                showOnCreate: true,
                offset: [0, 8],
                aria: {
                    content: null,
                    expanded: false,
                },
                interactive: true,
                popperOptions: {
                    modifiers: [
                        {
                            name: 'flip',
                            options: {
                                fallbackPlacements: ['top', 'bottom'],
                            },
                        },
                    ],
                },
                render() {
                    return {
                        popper: listBoxElement,
                    };
                },
            });
            this.tippyInstance.hide();
        }
    }
    initOptions() {
        this.options = Array.from(this.element.querySelectorAll(`${transformTagName('cosmos-option')}`));
        this.optgroups = Array.from(this.element.querySelectorAll(`${transformTagName('cosmos-optgroup')}`));
        const preSelectedOption = this.getSelectedOption();
        if (preSelectedOption) {
            this.selectOption(preSelectedOption, false);
        }
        else {
            this.updateOptions();
        }
    }
    updateOptions() {
        this.options.forEach((option, index) => {
            // Set tabIndex to -1 for all options to manage focus dynamically when using keyboard navigation.
            // When not using keyboard, set tabIndex to 0 to ensure all options are still accessible to AT.
            option.tabIndex = this.isNavigatedWithKeyboard ? -1 : 0;
            if (index !== this.selectedOptionIndex) {
                option.removeAttribute('selected');
            }
            if (index === this.selectedOptionIndex) {
                option.setAttribute('selected', '');
            }
            if (index === this.activeOptionIndex) {
                option.tabIndex = 0;
                option.focus();
            }
        });
    }
    connectedCallback() {
        this.form = this.element.closest('form');
        if (this.form) {
            this.form.addEventListener('formdata', this.handleFormData, true);
            this.form.addEventListener('submit', this.handleFormSubmit, true);
        }
    }
    disconnectedCallback() {
        if (this.form) {
            this.form.removeEventListener('formdata', this.handleFormData);
            this.form.removeEventListener('submit', this.handleFormSubmit);
        }
        global.window.removeEventListener('click', this.handleDocumentClick);
    }
    async componentWillLoad() {
        this.inputInvalid = this.invalid;
        await nextTick();
        this.initOptions();
        resolveAppearance({
            instance: this,
            modeToAppearanceMap: { dark: 'light', light: 'dark' },
            callback: this.handleAppearanceChange.bind(this),
        });
    }
    componentDidLoad() {
        this.initTippy();
        if (this.autofocus) {
            this.initAutofocus();
        }
    }
    render() {
        var _a;
        return (h("div", { class: generateClassNames({}), part: "base" }, h(FormField, { element: this.element, appearance: this.computedAppearance, label: this.label, helpText: this.helpText, optionalLabel: this.optionalLabel, requiredLabel: this.requiredLabel, disabled: this.disabled, invalid: this.inputInvalid, validationMessage: this.getValidationMessage(), labelForValue: "combobox", required: this.required }, h("div", { class: generateClassNames('input', {
                appearance: this.computedAppearance,
                placeholder: this.selectedOptionIndex === undefined,
                disabled: this.disabled,
                invalid: this.inputInvalid,
            }), onClick: this.handleSelectClick, ref: (el) => {
                this.inputElement = el;
            }, part: "select" }, h("div", { class: generateClassNames('combobox'), tabIndex: this.disabled ? -1 : 0, id: "combobox", role: "combobox", "aria-haspopup": "listbox", "aria-controls": this.tippyInstance && `tippy-${this.tippyInstance.id}`, "aria-expanded": getAriaExpanded(this.isListBoxVisible), "aria-disabled": getAriaDisabled(this.disabled), "aria-required": getAriaRequired(this.required), "aria-invalid": getAriaInvalid(this.inputInvalid), "aria-labelledby": "label", "aria-describedby": getAriaDescribedBy(this.inputInvalid), ref: (el) => {
                this.comboBoxElement = el;
            } }, this.selectedOptionElement && (h("div", { class: generateClassNames('sr-value') }, (_a = this.selectedOptionElement.textContent) === null || _a === void 0 ? void 0 : _a.trim())), h("slot", { name: "value", "aria-hidden": "true" }, h("cosmos-text", { class: generateClassNames('placeholder'), size: "small", clamp: true, kind: "subtle" }, this.placeholder))), h("div", { class: generateClassNames('focus-ring') }), !!this.clearable && !!this.selectedOptionElement && (h("cosmos-button", { "aria-label": this.clearAriaLabel, "aria-hidden": !this.clearAriaLabel, appearance: this.computedAppearance, class: generateClassNames('clearable-button'), kind: "tertiary", onClick: this.handleClearableClick, onKeyDown: this.handleClearableKeyDown, onMouseDown: (e) => e.preventDefault(), shape: "circle", size: "small" }, h("cosmos-icon-close", { slot: "icon" }))), h("cosmos-icon-arrow-down", { class: generateClassNames('icon') })), h("div", { onClick: this.handleListBoxClick, role: "listbox", tabIndex: -1, part: "options", class: generateClassNames('listbox', {
                appearance: this.computedAppearance,
                open: this.isListBoxVisible,
            }), ref: (el) => {
                this.listBoxElement = el;
            } }, h("slot", { onSlotchange: this.handleSlotChange })))));
    }
    get element() { return this; }
    static get watchers() { return {
        "invalid": ["handleInvalidChange"],
        "value": ["handleValueChange"],
        "appearance": ["handleAppearanceChange"]
    }; }
    static get style() { return CosmosSelectStyle0; }
}, [1, "cosmos-select", {
        "appearance": [513],
        "autofocus": [516],
        "clearable": [516],
        "clearAriaLabel": [513, "clear-aria-label"],
        "disabled": [516],
        "helpText": [513, "help-text"],
        "invalid": [516],
        "label": [513],
        "name": [513],
        "optionalLabel": [513, "optional-label"],
        "placeholder": [513],
        "required": [516],
        "requiredLabel": [513, "required-label"],
        "validationMessage": [513, "validation-message"],
        "value": [1537],
        "activeOptionElement": [32],
        "activeOptionIndex": [32],
        "computedAppearance": [32],
        "inputInvalid": [32],
        "isListBoxVisible": [32],
        "selectedOptionElement": [32],
        "selectedOptionIndex": [32],
        "inputBlur": [64],
        "inputFocus": [64],
        "inputCheckValidity": [64],
        "inputReportValidity": [64]
    }, [[0, "optionselect", "handleOptionSelect"], [0, "keydown", "handleKeyDown"]], {
        "invalid": ["handleInvalidChange"],
        "value": ["handleValueChange"],
        "appearance": ["handleAppearanceChange"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["cosmos-select", "cosmos-button", "cosmos-icon-arrow-down", "cosmos-icon-close", "cosmos-icon-warning", "cosmos-select-value", "cosmos-spinner", "cosmos-text"];
    components.forEach(tagName => { switch (tagName) {
        case "cosmos-select":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, CosmosSelect$1);
            }
            break;
        case "cosmos-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "cosmos-icon-arrow-down":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "cosmos-icon-close":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "cosmos-icon-warning":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "cosmos-select-value":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "cosmos-spinner":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "cosmos-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const CosmosSelect = CosmosSelect$1;
const defineCustomElement = defineCustomElement$1;

export { CosmosSelect, defineCustomElement };
